/**
 * Display helpers — keep all number/address formatting in one place so the
 * presentation stays consistent across pages.
 */

/** Truncate an Ethereum address to "0x1234…abcd" */
export function shortenAddress(addr?: string | null, leading = 6, trailing = 4): string {
  if (!addr) return '';
  if (addr.length <= leading + trailing) return addr;
  return `${addr.slice(0, leading)}…${addr.slice(-trailing)}`;
}

/** Format USDT amount with thousands separators and exactly 2 decimals. Callers add the "$". */
export function fmtUsdt(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === '') return '0.00';
  const num = typeof n === 'string' ? parseFloat(n) : n;
  if (!Number.isFinite(num)) return '0.00';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Format AIML integer-ish amount with thousands separators, no decimals (since bonuses are whole units). */
export function fmtAiml(n: number | string | bigint | null | undefined): string {
  if (n === null || n === undefined || n === '') return '0';
  let num: number;
  if (typeof n === 'bigint') {
    num = Number(n);
  } else if (typeof n === 'string') {
    num = parseFloat(n);
  } else {
    num = n;
  }
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** Format ISO date → short readable. */
export function fmtDate(iso?: string | Date | null): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function fmtDateTime(iso?: string | Date | null): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for older webviews (Capacitor Android < 5 sometimes lacks clipboard)
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
